/**
 * Utility for formatting reminder send times.
 * 
 * The backend stores reminder timestamps in UTC. This helper converts those
 * timestamps to a fixed GMT+2 display string while respecting the user's
 * configured timezone where necessary.
 */
export function formatNextSendTimeGMT2(
  isoString: string | null | undefined,
  timezone?: string
): string {
  if (!isoString) {
    return 'Not scheduled'
  }

  try {
    const date = new Date(isoString)
    if (isNaN(date.getTime())) {
      return isoString
    }

    const normalizedTimezone = (timezone || '').trim().toLowerCase()
    const isUTC = normalizedTimezone === 'utc' || normalizedTimezone === 'etc/utc' || normalizedTimezone === 'gmt'

    // Calculate the offset to display in GMT+2 (UTC+2)
    const offsetHours = isUTC ? -2 : 2
    const adjustedDate = new Date(date.getTime() + offsetHours * 60 * 60 * 1000)

    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    })

    const result = `${formatter.format(adjustedDate)} GMT+2`

    if (process.env.NODE_ENV === 'development') {
      console.debug('formatNextSendTimeGMT2', {
        isoString,
        timezone,
        normalizedTimezone,
        utcTime: date.toISOString(),
        gmtPlus2Time: adjustedDate.toISOString(),
        result
      })
    }

    return result
  } catch (error) {
    console.error('Error formatting next send time:', error, { isoString, timezone })
    return isoString
  }
}
